import { useState, useEffect } from "react";
import { 
  Settings, MessageCircle, Palette, Bell, Shield, 
  Check, X, Loader2, Send, Link2, Unlink
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [linkCode, setLinkCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [branding, setBranding] = useState({
    company_name: "YASNO Marketing",
    logo_url: "",
    primary_color: "#00E5FF",
    secondary_color: "#1a1a2e",
    font: "Inter",
    contact_email: "",
    website: ""
  });
  const [telegramSettings, setTelegramSettings] = useState({
    enabled: false,
    notify_before_post: true,
    auto_approve_after_hours: 24
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [telegramRes, brandingRes] = await Promise.all([
        axios.get(`${API}/telegram/status`, { headers }),
        axios.get(`${API}/reports/branding`, { headers })
      ]);

      setTelegramStatus(telegramRes.data);
      if (telegramRes.data.settings) {
        setTelegramSettings(telegramRes.data.settings);
      }
      if (brandingRes.data.branding) {
        setBranding(brandingRes.data.branding);
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveTelegramSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/settings`, telegramSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Telegram settings saved!");
    } catch (error) {
      toast.error("Failed to save Telegram settings");
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reports/branding`, branding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Branding settings saved!");
    } catch (error) {
      toast.error("Failed to save branding settings");
    } finally {
      setSaving(false);
    }
  };

  const linkTelegram = async () => {
    if (!linkCode.trim()) {
      toast.error("Please enter the verification code");
      return;
    }
    setLinking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/telegram/link`, 
        { code: linkCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Telegram account linked successfully!");
      setLinkCode("");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired code");
    } finally {
      setLinking(false);
    }
  };

  const unlinkTelegram = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/unlink`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Telegram account unlinked");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to unlink Telegram");
    }
  };

  const testTelegramNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test notification sent to your Telegram!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Telegram Human-in-the-Loop */}
      <Card className="glass-card border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <MessageCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Telegram Integration</CardTitle>
                <CardDescription>Human-in-the-Loop content approval via Telegram</CardDescription>
              </div>
            </div>
            {telegramStatus?.bot_configured ? (
              <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                <Check className="w-3 h-3 mr-1" /> Bot Active
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">
                <X className="w-3 h-3 mr-1" /> Not Configured
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connection Status */}
          {telegramStatus?.bot_configured && (
            <>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      Bot: @{telegramStatus.bot_info?.username || "YASNOMarketingBot"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {telegramStatus.user_connected 
                        ? `Connected as @${telegramStatus.settings?.username || 'User'}` 
                        : "Your account is not connected yet"}
                    </p>
                  </div>
                  {telegramStatus.user_connected ? (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={testTelegramNotification}
                        className="border-white/10"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Test
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={unlinkTelegram}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Unlink className="w-4 h-4 mr-2" />
                        Unlink
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                      Not Connected
                    </Badge>
                  )}
                </div>
              </div>

              {/* Link Account */}
              {!telegramStatus.user_connected && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <h4 className="font-medium text-blue-400 mb-2">How to Connect:</h4>
                    <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                      <li>Open Telegram and search for <span className="text-blue-400 font-mono">@{telegramStatus.bot_info?.username || "YASNOMarketingBot"}</span></li>
                      <li>Send <span className="text-blue-400 font-mono">/start</span> to the bot</li>
                      <li>Copy the verification code the bot gives you</li>
                      <li>Paste it below and click "Link Account"</li>
                    </ol>
                  </div>
                  
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter verification code (e.g., A1B2C3D4)"
                      value={linkCode}
                      onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                      className="bg-white/5 border-white/10 font-mono uppercase"
                      maxLength={8}
                    />
                    <Button 
                      onClick={linkTelegram} 
                      disabled={linking || !linkCode.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {linking ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Link2 className="w-4 h-4 mr-2" />
                          Link Account
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Settings */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-medium">Notification Settings</h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Telegram Notifications</Label>
                    <p className="text-xs text-muted-foreground">Receive content for approval via Telegram</p>
                  </div>
                  <Switch
                    checked={telegramSettings.enabled}
                    onCheckedChange={(checked) => setTelegramSettings(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Notify Before Posting</Label>
                    <p className="text-xs text-muted-foreground">Send content for review before auto-posting</p>
                  </div>
                  <Switch
                    checked={telegramSettings.notify_before_post}
                    onCheckedChange={(checked) => setTelegramSettings(prev => ({ ...prev, notify_before_post: checked }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Auto-approve after (hours)</Label>
                  <p className="text-xs text-muted-foreground">Content auto-approves if you don't respond within this time</p>
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={telegramSettings.auto_approve_after_hours}
                    onChange={(e) => setTelegramSettings(prev => ({ 
                      ...prev, 
                      auto_approve_after_hours: parseInt(e.target.value) || 24 
                    }))}
                    className="w-32 bg-white/5 border-white/10"
                  />
                </div>

                <Button onClick={saveTelegramSettings} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Telegram Settings
                </Button>
              </div>
            </>
          )}

          {!telegramStatus?.bot_configured && (
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-center">
              <p className="text-sm text-yellow-400">
                Telegram bot is not configured. Please contact support to set up the bot.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* White-Label Branding */}
      <Card className="glass-card border-white/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Palette className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-lg">White-Label Branding</CardTitle>
              <CardDescription>Customize reports with your company branding</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={branding.company_name}
                onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Your Company Name"
                className="bg-white/5 border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <Input
                value={branding.logo_url || ""}
                onChange={(e) => setBranding(prev => ({ ...prev, logo_url: e.target.value }))}
                placeholder="https://yoursite.com/logo.png"
                className="bg-white/5 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-12 h-10 p-1 bg-white/5 border-white/10 cursor-pointer"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  placeholder="#00E5FF"
                  className="bg-white/5 border-white/10 font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={branding.secondary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                  className="w-12 h-10 p-1 bg-white/5 border-white/10 cursor-pointer"
                />
                <Input
                  value={branding.secondary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                  placeholder="#1a1a2e"
                  className="bg-white/5 border-white/10 font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={branding.contact_email || ""}
                onChange={(e) => setBranding(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="contact@yourcompany.com"
                className="bg-white/5 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={branding.website || ""}
                onChange={(e) => setBranding(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://yourcompany.com"
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 rounded-lg border border-white/10" style={{ backgroundColor: branding.secondary_color }}>
            <p className="text-xs text-muted-foreground mb-2">Preview</p>
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center text-xs">Logo</div>
              )}
              <span className="font-semibold" style={{ color: branding.primary_color }}>
                {branding.company_name}
              </span>
            </div>
          </div>

          <Button onClick={saveBranding} disabled={saving} className="w-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Branding Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
